import type * as React from 'react';

import { StatusChip } from '@/design';
import type {
  IAdminParticipantAccount,
  IAdminParticipantHub
} from '../../api/participant-contracts';
import {
  AccountStateChip,
  accountStates,
  ownAccounts,
  relationWords
} from '../../components/account-facts';
import { Fact } from '../../components/admin-page';
import { CopyableValue } from '../../components/copyable-value';
import { RegistryNumberChip } from '../../components/registry-number-chip';
import { UnderReviewChip } from '../../components/under-review-chip';
import { registryNumbers } from '../../registries';
import { DisclosureSummary, disclosureSummaryOf } from './disclosure-summary';
import { profileIsEmpty, profilePlace, surveysOf } from './household';
import { PhotoControls } from './photo';
import { RecordDates } from './record-dates';
import { ResearchHeadline } from './research-headline';
import { routingChip } from './team';

/**
 * **On a record page this is the record card, above the tabs** (SR-406): who the record is on the
 * left, the Actions menu and the summary (`summaryPanel`) on the right, and the four dates across
 * the whole card under both, their notes behind "About these dates" — the same card on every tab.
 * It draws no "Under review" chip there: the summary's compliance line says it, with the item. What
 * follows describes the facts both pages draw, and the household's page's one column.
 *
 * The hub's answer to "who is this", under the page's title (the participant's display name):
 * where they are, the address they sign in with, and chips — the kind, each registry number
 * with where it stands, its disclosures at a glance (`disclosure-summary.tsx`, SR-216), the
 * surveys the household holds, Veritant's headline staff fields (Approval status, Quality rating,
 * Travel — `research-headline.tsx`, the F2 plan's R11), and each of the participant's own (`self`)
 * accounts' login state in `account-facts.tsx`'s words (R6), "No account" where none is its own —
 * the others said apart by relation, under "Points of contact", "Assistants" and "Listed on the
 * roster" (SR-416; SR-487 ruling 19) — and, for a
 * team, where survey-api routes it and whether it is archived (the teams onboarding plan's R12),
 * in `team.tsx`'s words — and whether survey-api counts them under review (SR-222, F9's R11), as
 * sent.
 *
 * **A profile of nulls is said, never interpreted.** survey-api sends the profile with every
 * member null where no questionnaire filled it, and on a migrated participant the title is then
 * their login standing in for a name — which this page cannot know and does not claim. It says
 * only that nothing has named them yet.
 *
 * The numbers are the opened participant's (the rest of the household's are on Overview); on a
 * team's or firm's household page the surveys are the household's, read at a glance as
 * "TEAMS · FA". A person's record page (SR-391) draws neither the surveys nor the headline chips
 * here: its surveys are its tabs, and each tab's strip carries that survey's chips. Nothing is
 * summarised into a verdict: an account none of whose states applies shows its address and no
 * chip.
 *
 * Below the place, the record's id and dates (the advisor record page's F11): the participant id,
 * copyable (R4, IAM-08 — the GUID; no shorter id exists), and REC-06's four dates in
 * `record-dates.tsx`' words, which say what survey-api's members are and what is not recorded yet.
 *
 * What the header does rather than says: its first row sets, replaces and removes the opened
 * participant's photo (`photo.tsx`, SR-222's block 8 — the controls alone, since the portrait is
 * the title's), over the page's `reread`; and `actions` — "Ask registries again"
 * (`numbers.tsx`), whose answers are said beside each number, below.
 */
export function HubHeader({
  hub,
  reread,
  actions,
  recordPage = false,
  summaryPanel
}: {
  hub: IAdminParticipantHub;
  reread: () => Promise<boolean>;
  actions?: React.ReactNode;
  /** A person's record page (SR-391): the surveys are its tabs and their headline values sit in
   *  each tab's strip, so the header draws neither. */
  recordPage?: boolean;
  /** A record page's summary (SR-400), drawn inside this card below the facts — one record card,
   *  the same on every tab, above the tabs (SR-406). */
  summaryPanel?: React.ReactNode;
}) {
  const { participant } = hub;
  const place = profilePlace(participant.profile);
  const numbers = registryNumbers(participant.identifiers);
  const surveys = surveysOf(hub.responses);
  const summary = disclosureSummaryOf(hub, participant.participantId);
  // The participant's own accounts are its way in; a point of contact signs in as itself, so its
  // login state is said apart and never stands in for "No account" (SR-416, F1). The others are
  // grouped by how they act for it (SR-487 ruling 19).
  const own = ownAccounts(participant.accounts);
  const others = accountGroupsOf(participant.accounts);

  const recordCard = Boolean(summaryPanel);
  const facts = (
    <>
      {!recordCard && <PhotoControls participant={participant} reread={reread} />}
      {profileIsEmpty(participant.profile) ? (
        <p className="text-muted-foreground">No questionnaire has named this participant yet.</p>
      ) : (
        place && <p className="font-medium">{place}</p>
      )}
      {own.length > 0 && (
        <p className="break-all text-muted-foreground">
          {own.map((account) => account.email).join(' · ')}
        </p>
      )}
      {others.map(({ relation, title, accounts }) => (
        <div
          key={relation}
          className="grid gap-1"
          data-account-group={relation}
          data-points-of-contact={relation === 'contact' ? '' : undefined}
        >
          <span className="text-xs text-muted-foreground uppercase">{title}</span>
          <ul aria-label={title} className="flex flex-wrap items-center gap-2">
            {accounts.map((account) => (
              <li key={account.accountId} className="flex flex-wrap items-center gap-2">
                <span className="break-all">{account.email}</span>
                {accountStates(account).map((state) => (
                  <AccountStateChip key={state} state={state} />
                ))}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {recordCard ? (
        // The id is looked up now and then and copied rarely: small, under the address (SR-407).
        <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span className="uppercase">Id</span>
          <CopyableValue
            value={participant.participantId}
            name="Copy the participant id"
            compact
          />
        </p>
      ) : (
        <Fact
          label="Participant id"
          value={<CopyableValue value={participant.participantId} name="Copy the participant id" />}
        />
      )}
      {!summaryPanel && (
        <RecordDates participant={participant} responses={hub.responses} header />
      )}
      <ul aria-label="At a glance" className="flex flex-wrap items-center gap-2">
        <li>
          <StatusChip>{participant.kind}</StatusChip>
        </li>
        {numbers.map((number) => (
          <li key={`${number.idType} ${number.value}`}>
            <RegistryNumberChip number={number} />
          </li>
        ))}
        {summary !== undefined && (
          <li>
            <DisclosureSummary summary={summary} />
          </li>
        )}
        {participant.teamRouting && (
          <li>
            <StatusChip tone="info">{routingChip(participant.teamRouting)}</StatusChip>
          </li>
        )}
        {/* A record page says it in its summary's compliance line, with the item and who (SR-406). */}
        {participant.underReview === true && !recordPage && (
          <li>
            <UnderReviewChip underReview />
          </li>
        )}
        {participant.archive && (
          <li>
            <StatusChip tone="danger">Archived</StatusChip>
          </li>
        )}
        {surveys.length > 0 && !recordPage && (
          <li>
            <StatusChip tone="info">{surveys.join(' · ')}</StatusChip>
          </li>
        )}
        {!recordPage && <ResearchHeadline hub={hub} />}
        {own.length === 0 ? (
          <li>
            <AccountStateChip state="no account" />
          </li>
        ) : (
          own.flatMap((account) =>
            accountStates(account).map((state) => (
              <li key={`${account.accountId} ${state}`}>
                <AccountStateChip state={state} />
              </li>
            ))
          )
        )}
      </ul>
      {/* The record card's photo controls are its last line, link-weight (SR-407). */}
      {recordCard && <PhotoControls participant={participant} reread={reread} quiet />}
    </>
  );

  // A record page's card (SR-406): who the record is on the left, its state — the Actions menu
  // and the summary — on the right, one card above the tabs. The household's page keeps the one
  // column, its button under the facts.
  if (summaryPanel) {
    return (
      <section
        aria-label="Who this is"
        className="grid gap-4 rounded-lg border border-border bg-card p-4 text-sm lg:grid-cols-2"
      >
        <div data-record-identity className="grid min-w-0 content-start gap-3">
          {facts}
        </div>
        <div
          data-record-state
          className="grid min-w-0 content-start gap-3 border-t border-border pt-3 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-4"
        >
          {actions}
          {summaryPanel}
        </div>
        {/* The dates across the whole card, under both columns — Lightning's highlights strip. */}
        <div className="border-t border-border pt-3 lg:col-span-2">
          <RecordDates participant={participant} responses={hub.responses} header compact />
        </div>
      </section>
    );
  }
  return (
    <section
      aria-label="Who this is"
      className="grid gap-3 rounded-lg border border-border bg-card p-4 text-sm"
    >
      {facts}
      {actions}
    </section>
  );
}

/** The heading of each group of accounts that act for a participant other than as its own. */
const GROUP_TITLES: Readonly<Record<string, string>> = {
  contact: 'Points of contact',
  assistant: 'Assistants',
  member: 'Listed on the roster'
};

/** The accounts that are not the participant's own, grouped by their relation in the order the
 *  card first names one — a relation this app does not know headed by its spelling. */
function accountGroupsOf(accounts: IAdminParticipantAccount[]): {
  relation: string;
  title: string;
  accounts: IAdminParticipantAccount[];
}[] {
  const groups = new Map<string, IAdminParticipantAccount[]>();
  for (const account of accounts) {
    if (account.relation === 'self') continue;
    groups.set(account.relation, [...(groups.get(account.relation) ?? []), account]);
  }
  return [...groups].map(([relation, held]) => ({
    relation,
    title: GROUP_TITLES[relation] ?? relationWords(relation) ?? relation,
    accounts: held
  }));
}
